import { ProductSlug } from "@/api/product-api";

/**
 * 번들 → 베이스 제품 매핑 (번들 PDP → 베이스 제품 + `?bundle=` 리다이렉트용).
 *
 * 번들은 여러 제품으로 구성돼 있어 "어느 페이지로 보낼지"를 자동으로 정할 수 없다
 * (예: arizona-bundle = tactsuit-air + asr-badge, fps-bundle = tactsuit-pro + tactsleeve).
 * 자동 추론은 조용히 틀릴 수 있고 여기는 구매 진입 경로라, 기획이 정한 값을 명시 선언한다.
 *
 * `color` 는 베이스가 camo 변형인 경우 함께 넘긴다 — camo 는 별도 제품이지만 URL 은
 * 기본 제품의 `?color=camo` 로 통합돼 있기 때문(`camo-variant.ts` 참조).
 *
 * ⚠️ 여기 없는 번들은 리다이렉트하지 않고 **자체 PDP 를 그대로 쓴다** — 매핑을 모르는
 * 번들을 임의의 페이지로 보내지 않기 위함. 새 번들 추가 시 여기 한 줄 추가.
 *
 * ## ★왜 공유 모듈인가 (#5798)
 *
 * 종전엔 `pages/shop/buy-tactsuit/[slug].tsx` 의 **지역 상수**였다. 그래서 목적지를 아는
 * 곳이 그 페이지 하나뿐이었고, **링크를 만드는 쪽은 번들 slug 를 그대로 방출**했다 —
 * 클릭마다 301 을 한 번 더 탔다(prod 실측 2026-09-18: `/ko/shop/buy-tactsuit/fps-bundle/`
 * → 홉 2 → `…/tactsuit-pro/?bundle=fps-bundle`).
 *
 * `camo-variant.ts` 가 **정확히 같은 이유로** 먼저 꺼내진 모듈이다(#5386 — 그 docblock:
 * *"사본을 늘리면 갈린다 … 그래서 여기 하나만 둔다"*). 이 맵도 같은 자리에 둔다.
 *
 * ⚠️ **`-bundle` 접미사로 추론하지 않는다.** 이 맵에 없는 번들은 자체 PDP 를 쓰고,
 * 이름에 `camo` 가 들어간 액세서리(`original-camo-badge-for-tactsuit-air`)는 변형이
 * 아니다 — prod 실측(2026-09-18)에서 그 슬러그는 **홉 0**이다. 패턴 추론은 둘 다 틀린다
 * (`docs/product-variant-line-policy.md` §2 「매핑을 하드코딩한다」).
 */
export const BUNDLE_BASE_BY_SLUG: Partial<
  Record<string, { base: ProductSlug; color?: string }>
> = {
  [ProductSlug.ARIZONA_BUNDLE]: { base: ProductSlug.TACTSUIT_AIR },
  [ProductSlug.STARTER_BUNDLE]: { base: ProductSlug.TACTSUIT_AIR },
  [ProductSlug.STARTER_BUNDLE_CAMO]: {
    base: ProductSlug.TACTSUIT_AIR,
    color: "camo",
  },
  [ProductSlug.FPS_BUNDLE]: { base: ProductSlug.TACTSUIT_PRO },
  [ProductSlug.FPS_BUNDLE_TACTVISOR]: { base: ProductSlug.TACTSUIT_PRO },
  [ProductSlug.METRO_BUNDLE]: { base: ProductSlug.TACTSUIT_PRO },
  // 글러브 사이즈 세트 — 자체 PDP 는 가격·담기뿐이라(제품 정보·사이즈 가이드·구성 없음) 글러브 PDP 의
  // 세트 칩으로 이관한다. 세트를 골라 두는 판정은 `glove-set.ts` 의 `resolvePreselectedGloveSet`.
  // DK2 세트는 판매 종료라 넣지 않는다(2026-10-16 작성자 확인).
  [ProductSlug.TACTGLOVE_DK3_BUNDLE]: { base: ProductSlug.TACTGLOVE_DK3 },
};

/**
 * 번들 slug 가 이관될 **쿼리 문자열**(`bundle=…[&color=…]`). 번들이 아니면 `undefined`.
 *
 * 링크 생성(`getProductDetailHref`)과 PDP 의 리다이렉트가 **같은 값을 쓰게** 하는 자리다.
 * 둘이 갈리면 링크는 A 로 보내는데 리다이렉트는 B 로 보내는 상태가 되고, 그 차이는
 * 화면상 아무 증상도 안 낸다(둘 다 200 이다).
 */
export const bundleTargetOf = (
  slug: string,
): { base: string; query: string } | undefined => {
  const target = BUNDLE_BASE_BY_SLUG[slug];
  if (!target) return undefined;
  const query = new URLSearchParams({ bundle: slug });
  if (target.color) query.set("color", target.color);
  return { base: target.base, query: query.toString() };
};
